import { get, post } from "./request";
import { buildApiUrl, API_CONFIG } from "./config";
import type {
  ApiResponse,
  UserInfo,
  LoginParams,
  UserListParams,
} from "./types";
import { NoticeTab } from "@/stores/user";

// 用户相关API
export const userApi = {
  /**
   * 登录
   * @param params - 登录参数，包含 token、game_id 和其他 URL 参数
   * @returns Promise<ApiResponse<UserInfo>>
   */
  login: (params: LoginParams): Promise<ApiResponse<UserInfo>> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.TOKEN_LOGIN,
      params
    );
    return get<ApiResponse<UserInfo>>(url);
  },

  /**
   * 登出
   * @returns Promise<ApiResponse>
   */
  logout: (): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.LOGOUT
    );
    return get<ApiResponse>(url);
  },

  /**
   * 获取用户信息
   * @returns Promise<ApiResponse<UserInfo>>
   */
  getInfo: (game_id: number, uid?: number): Promise<ApiResponse<UserInfo>> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.INFO,
      { uid: uid || undefined, game_id: game_id }
    );
    return get<ApiResponse<UserInfo>>(url);
  },

  /**
   * 获取所有用户
   * @param params - 分页参数
   * @returns Promise<ApiResponse<UserInfo[]>>
   */
  getList: (params?: UserListParams): Promise<ApiResponse<UserInfo[]>> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.LIST,
      params
    );
    return get<ApiResponse<UserInfo[]>>(url);
  },

  /**
   * 搜索用户
   * @param params - 搜索参数
   * @returns Promise<ApiResponse<UserInfo[]>>
   */
  search: (params?: UserListParams): Promise<ApiResponse<UserInfo[]>> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.SEARCH,
      params
    );
    return get<ApiResponse<UserInfo[]>>(url);
  },

  /**
   * 更新用户真实姓名
   * @param realname - 真实姓名
   * @returns Promise<ApiResponse>
   */
  updateRealName: (realname: string, game_id: number): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.UPDATE_REAL_NAME,
      { realname, game_id }
    );
    return post<ApiResponse>(url, { realname, game_id });
  },

  /**
   * 更新用户头像
   * @param avatar - 头像
   * @returns Promise<ApiResponse>
   */
  updateAvatar: (avatar: string, game_id: number): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.UPDATE_AVATAR,
      { avatar, game_id }
    );
    return post<ApiResponse>(url, { avatar, game_id });
  },
  /**
   * 关注用户
   * @param uid - 用户ID
   * @param game_id - 游戏ID
   * @returns Promise<ApiResponse>
   */
  follow: (fuid: number, game_id: number): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.FOLLOW,
      { fuid, game_id }
    );
    return post<ApiResponse>(url, { fuid, game_id });
  },

  /**
   * 取消关注用户
   * @param uid - 用户ID
   * @param game_id - 游戏ID
   * @returns Promise<ApiResponse>
   */
  unfollow: (fuid: number, game_id: number): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.UNFOLLOW,
      { fuid, game_id }
    );
    return post<ApiResponse>(url, { fuid, game_id });
  },

  /**
   * 获取关注列表
   * @param uid - 用户ID
   * @returns Promise<ApiResponse>
   */
  getFollowList: (
    uid: number,
    type: "following" | "followers",
    game_id: number
  ): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.FOLLOW_LIST,
      { uid, type, game_id }
    );
    return get<ApiResponse>(url);
  },

  /**
   * 获取用户信息通知数量
   * @param uid - 用户ID
   * @returns Promise<ApiResponse>
   */
  getInfoNotificationCount: (
    uid: number,
    game_id: number
  ): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.NOTIFICATION_COUNT,
      { uid, game_id }
    );
    return get<ApiResponse>(url);
  },

  /**
   * 获取用户信息通知详情
   * @param uid - 用户ID
   * @returns Promise<ApiResponse>
   */
  getInfoNotificationDetail: (
    type: NoticeTab,
    game_id: number,
    page: number = 1,
    page_size: number = 10
  ): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.NOTIFICATION_DETAIL,
      { module: type, page, page_size, game_id }
    );
    return get<ApiResponse>(url);
  },

  /**
   * 获取用户关注通知数量
   * @param uid - 用户ID
   * @returns Promise<ApiResponse>
   */
  getFollowNotificationCount: (game_id: number): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.FOLLOW_NOTIFICATION,
      { game_id }
    );
    return get<ApiResponse>(url);
  },

  /**
   * 获取用户真实姓名修改记录
   * @returns Promise<ApiResponse>
   */
  getRealNameRecord: (game_id: number): Promise<ApiResponse> => {
    const url = buildApiUrl(
      API_CONFIG.CONTROLLERS.USER,
      API_CONFIG.ACTIONS.GET_REAL_NAME_RECORD,
      { game_id }
    );
    return get<ApiResponse>(url);
  },
};
